<template>
  <v-card>
    <v-card-text>
      <v-progress-linear
        v-if="loadingConfigs"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <v-text-field
        v-if="configs.length > 0"
        v-model="searchQuery"
        label="Search configs..."
        prepend-inner-icon="mdi-magnify"
        outlined
        clearable
        dense
        class="mb-4"
      ></v-text-field>

      <v-expansion-panels
        v-if="configs.length > 0"
        v-model="openPanels"
        multiple
        class="mb-2"
      >
        <v-expansion-panel
          v-for="(categoryConfigs, category) in filteredConfigsByCategory"
          :key="category"
        >
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              <span class="text-h6">{{ category }}</span>
              <v-chip small class="ml-2">{{ categoryConfigs.length }}</v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card flat outlined class="pa-2">
              <v-card-text>
                <div v-for="config in categoryConfigs" :key="config.name">
                  <div
                    style="text-align: right"
                    v-if="config.updatedByUsername"
                  >
                    Updated by {{ config.updatedByUsername }} on
                    {{ date(config.updated_at) }}
                  </div>
                  <div>
                    <v-text-field
                      v-if="config.value !== true && config.value !== false"
                      v-model="config.value"
                      :type="
                        isContactConfig(config)
                          ? 'text'
                          : isNumeric(config.value)
                          ? 'number'
                          : 'text'
                      "
                      filled
                    >
                      <template v-slot:label>
                        <div>
                          <span style="display: inline-block; font-size: 20px">
                            {{ config.name }}
                          </span>
                        </div>
                      </template>
                    </v-text-field>
                    <v-checkbox
                      style="margin-top: 0px"
                      v-else
                      v-model="config.value"
                      :label="config.name"
                    >
                      <template v-slot:label>
                        <div>
                          {{ config.name }}
                        </div>
                      </template>
                    </v-checkbox>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-btn
        color="primary"
        outlined
        block
        large
        class="mb-2"
        :loading="loading"
        :disabled="!updated"
        @click="update()"
        >Save</v-btn
      >
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
export default {
  name: "Config",
  props: {
    message: Function,
    errorMessage: Function,
    configs: Array,
    getConfigs: Function,
    loadingConfigs: Boolean,
  },
  data: () => ({
    originalConfigs: [],
    newConfigs: [],
    loading: false,
    searchQuery: "",
    openPanels: [],
  }),
  computed: {
    updated() {
      return (
        JSON.stringify(this.originalConfigs) !== JSON.stringify(this.newConfigs)
      );
    },
    configsByCategory() {
      const grouped = {};
      this.newConfigs.forEach((config) => {
        const category = config.category;
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(config);
      });
      return grouped;
    },
    filteredConfigsByCategory() {
      if (!this.searchQuery || this.searchQuery.trim() === "") {
        return this.configsByCategory;
      }

      const query = this.searchQuery.toLowerCase();
      const filtered = {};

      Object.keys(this.configsByCategory).forEach((category) => {
        const categoryConfigs = this.configsByCategory[category].filter(
          (config) => {
            return (
              config.name.toLowerCase().includes(query) ||
              (config.description &&
                config.description.toLowerCase().includes(query)) ||
              category.toLowerCase().includes(query)
            );
          }
        );

        if (categoryConfigs.length > 0) {
          filtered[category] = categoryConfigs;
        }
      });

      return filtered;
    },
  },
  watch: {
    searchQuery() {
      if (this.searchQuery && this.searchQuery.trim() !== "") {
        this.openPanels = Object.keys(this.filteredConfigsByCategory).map(
          (_, index) => index
        );
      }
    },
  },
  methods: {
    shouldScaleNumericConfig(config) {
      const name = (config?.name ?? "").toString();
      if (name === "Reservation timeout in minutes") {
        return true;
      }
      if (name.includes("Cent") || name.includes("Laari")) {
        return true;
      }
      return false;
    },
    isContactConfig(config) {
      const name = (config?.name ?? "").toString();
      const category = (config?.category ?? "").toString();
      return category === "Contact" || name.startsWith("Contact ");
    },
    isNumeric(value) {
      // Check if the value is a number or can be converted to a valid number
      return !isNaN(value) && !isNaN(parseFloat(value)) && isFinite(value);
    },
    date(date) {
      return moment(date).format("YYYY-MM-DD HH:mm");
    },
    update() {
      this.loading = true;
      const data = this.newConfigs.map((c) => {
        let value;
        if (this.isContactConfig(c)) {
          value = c.value.toString();
        } else if (this.isNumeric(c.value)) {
          // Handle numeric values
          const num = parseFloat(c.value);
          if (this.shouldScaleNumericConfig(c)) {
            value = `${Math.round(num * 100)}`;
          } else {
            value = `${Math.round(num)}`;
          }
        } else if (c.value === true) {
          value = "true";
        } else if (c.value === false) {
          value = "false";
        } else {
          // Handle string values
          value = c.value.toString();
        }
        return { id: c.id, value };
      });
      axios({
        url: `${process.env.VUE_APP_BASE_URL ?? ""}/api/updateconfig`,
        method: "POST",
        headers: { Authorization: localStorage.getItem("Authorization") },
        data: JSON.stringify(data),
      })
        .then((resp) => {
          this.message(resp.data.message);
          this.newConfigs = [];
          this.getConfigs();
        })
        .catch((error) => {
          if (error.response) {
            this.errorMessage(error.response.data.error);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    init() {
      const conf = JSON.parse(JSON.stringify(this.configs));
      conf.forEach((c) => {
        if (this.isContactConfig(c)) {
          c.value = (c.value ?? "").toString();
        } else if (this.isNumeric(c.value)) {
          // Handle numeric values
          const num = parseFloat(c.value);
          c.name = c.name.replace("Cent", "USD");
          c.name = c.name.replace("Laari", "MVR");
          c.value = this.shouldScaleNumericConfig(c) ? num / 100 : num;
        } else if (c.value === "true") {
          c.value = true;
        } else if (c.value === "false") {
          c.value = false;
        }
        // String values remain as strings (no conversion needed)
      });
      const bools = [];
      this.newConfigs = [];
      conf.forEach((c) => {
        if (c.value === true || c.value === false) {
          bools.push(c);
        } else {
          this.newConfigs.push(c);
        }
      });
      bools.forEach((c) => {
        this.newConfigs.push(c);
      });
      this.originalConfigs = JSON.parse(JSON.stringify(this.newConfigs));
    },
  },
  mounted() {
    this.init();
  },
  watch: {
    configs: {
      handler: function () {
        this.init();
      },
    },
  },
};
</script>
